/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_INFINITE_LOOP_USERS_PAGE
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Fixed infinite loop by memoizing setParams function with useCallback
 *
 * File Size Check:
 * - Current: 138 lines
 * - Adding: ~1 line (import)
 * - Projected: 139 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/admin/users/UsersFilters.tsx (uses setParams as onChange)
 * - client/src/pages/admin/UsersPage.tsx (uses setParams)
 */

import { useSearchParams } from "react-router-dom";
import { useMemo, useCallback } from "react";
import { UserFilters } from "../../../../interfaces/adminUsers";

/**
 * Hook for managing URL search parameters for user filters
 * Provides utilities to read, coerce, and write URLSearchParams safely
 */
export function useUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to safely parse a string to number with fallback
  const parseNumber = (value: string | null, fallback: number): number => {
    if (!value) return fallback;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  };

  // Memoize params to prevent creating new object on every render
  const params = useMemo((): UserFilters => {
    return {
      q: searchParams.get("q") || undefined,
      role: (searchParams.get("role") as "admin" | "user") || undefined,
      status: (searchParams.get("status") as "active" | "suspended" | "pending") || undefined,
      verified: (searchParams.get("verified") as "1" | "0") || undefined,
      dateFrom: searchParams.get("dateFrom") || undefined,
      dateTo: searchParams.get("dateTo") || undefined,
      page: parseNumber(searchParams.get("page"), 1),
      // [2026-06-26] users-default-page-size: default rows per page is 10
      limit: parseNumber(searchParams.get("limit"), 10),
      sortBy: searchParams.get("sortBy") || "createdAt",
      sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "desc",
    };
  }, [searchParams]);

  // Update URL with new filter parameters
  const setParams = useCallback((patch: Partial<UserFilters>) => {
    const currentParams = new URLSearchParams(searchParams);

    // Apply the patch
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        currentParams.delete(key);
      } else {
        currentParams.set(key, value.toString());
      }
    });

    // Normalize values
    const page = parseNumber(currentParams.get("page"), 1);
    const limit = parseNumber(currentParams.get("limit"), 10);

    // Ensure page is at least 1
    if (page < 1) {
      currentParams.set("page", "1");
    }

    // Cap limit between 1 and 100
    if (limit < 1) {
      currentParams.set("limit", "1");
    } else if (limit > 100) {
      currentParams.set("limit", "100");
    }

    // Validate role
    const role = currentParams.get("role");
    if (role && !["admin", "user"].includes(role)) {
      currentParams.delete("role");
    }

    // Validate status
    const status = currentParams.get("status");
    if (status && !["active", "suspended", "pending"].includes(status)) {
      currentParams.delete("status");
    }

    // Validate verified
    const verified = currentParams.get("verified");
    if (verified && !["1", "0"].includes(verified)) {
      currentParams.delete("verified");
    }

    // Validate sortOrder
    const sortOrder = currentParams.get("sortOrder");
    if (sortOrder && !["asc", "desc"].includes(sortOrder)) {
      currentParams.set("sortOrder", "desc");
    }

    // Validate date range
    const dateFrom = currentParams.get("dateFrom");
    const dateTo = currentParams.get("dateTo");
    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      if (fromDate > toDate) {
        currentParams.delete("dateFrom");
        currentParams.delete("dateTo");
      }
    }

    setSearchParams(currentParams);
  }, [searchParams, setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setParams({
      q: undefined,
      role: undefined,
      status: undefined,
      verified: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      page: 1,
      limit: 10,
      sortBy: "createdAt",
      sortOrder: "desc",
    });
  }, [setParams]);

  // Check if any filters are active
  const hasActiveFilters = useCallback((): boolean => {
    return !!(
      params.q ||
      params.role ||
      params.status ||
      params.verified ||
      params.dateFrom ||
      params.dateTo
    );
  }, [params]);

  return {
    params,
    setParams,
    clearFilters,
    hasActiveFilters,
  };
}
